import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import { TranslateService } from '@ngx-translate/core';

import { AccountService } from '@services/account.service';
import { environment } from '@environments/environment';
import { ProfileService } from '@features/profile/profile.service';
import { OFFER_STATUSES } from '@config/offer-statuses.config';
import { OFFER_STATUSES_WTC } from '@config/offer-statuses-wtc.config';
import { OFFER_STATUSES_KOLIBRI } from '@config/offer-statuses-kolibri.config';
import { OfferApiService } from '@services/remote-api';
import { SseEventEnvelope, SseService } from '@services/remote-api/sse.service';
import { Config } from '@config/config.service';
import { OfferInfo, OfferMetadata, PermissionList } from './offer-info/offer-info.interface';
import { COMPLETE_STATUSES_MAP } from '@config/sessions.config';
import { OFFER_PRICE_SSE_EVENT_NAMES, OfferPriceSsePayload } from './offer-sse-events.config';
import {
  KOLIBRI_DECLINED_ADVISOR_REASON_KEY_LIST_1_TO_7,
  KOLIBRI_DECLINED_ADVISOR_REASON_KEY_LIST_8_TO_10,
  KOLIBRI_DECLINED_ADVISOR_REASON_STATUS_KEYS_1_TO_7,
  KOLIBRI_DECLINED_ADVISOR_REASON_STATUS_KEYS_8_TO_10,
} from '@config/declined-advisor-reason-kolibri.config';

@Injectable()
export class OfferService {
  private readonly offerPriceSseChannel = 'offer-private-hub-sse-offer-price-events';

  openTiaErrorProduct$: Subject<any> = new Subject();
  setNewOffer$: Subject<any> = new Subject();
  refreshOfferEvents$: Subject<any> = new Subject();
  productsMap: any;
  offerStatuses: any;
  currentOfferData: OfferInfo | null = null;
  isEditView = false;
  permissionsList: PermissionList;

  constructor(
    private accountService: AccountService,
    private translateService: TranslateService,
    private profileService: ProfileService,
    private offerApiService: OfferApiService,
    private sseService: SseService,
    private config: Config
  ) {
    this.productsMap = this.profileService.fetchProductsMap();
    this.offerStatuses = this.fetchOfferStatusesConfig();
  }

  fetchSsnValue() {
    return this.currentOfferData.ssn ? this.currentOfferData.ssn :
      this.currentOfferData.offerJson && this.currentOfferData.offerJson.acceptOfferData ?
        this.currentOfferData.offerJson.acceptOfferData.ssn : '';
  }

  changeClientDataDisabled(): boolean {
    return !this.currentOfferData.offerJson && this.currentOfferData.completeStatus === COMPLETE_STATUSES_MAP.CLIENT_ACTION_PENDING;
  }

  isFeatureEnabled(feature: string, permissionList: PermissionList): boolean {
    return permissionList.find(permission => permission.feature === feature).enabled;
  }

  getPermissions(): Observable<any> {
    return this.offerApiService.getPermissions();
  }

  getOfferPermissions(offerId: string): Observable<any> {
    return this.offerApiService.getOfferPermissions(offerId);
  }

  getBrokrPartnersList(offerId: string): Observable<any> {
    return this.offerApiService.getBrokrPartnersList(offerId);
  }

  updateSelectedBrokrPartnersList(offerId: string, partnerListId: any[]): Observable<any> {
    return this.offerApiService.updateSelectedBrokrPartnersList(offerId, partnerListId);
  }

  getDiscountList(listRange = [-50, 95], isStringValues = false): any {
    const list = [];

    for (let i = listRange[0]; i <= listRange[1]; i++) {
      isStringValues ? list.push(i + '') : list.push(i);
    }

    return list;
  }

  setNewOfferData(offerData: OfferInfo): void {
    this.setNewOffer$.next(offerData);
    this.refreshOfferEvents$.next(true);
  }

  generateUserUiUrl(): string {
    let baseUrl = '';

    if (environment.production) {
      baseUrl = this.accountService.getUser().userUiUrl.prod;
    } else {
      baseUrl = this.accountService.getUser().userUiUrl.dev;
    }

    return baseUrl;
  }

  fetchOfferStatusesConfig(): any {
    let offerStatusesConfig = {};

    switch (this.accountService.getUser().customerData.name) {
      case 'WATERCIRCLES': {
        offerStatusesConfig = OFFER_STATUSES_WTC;
        break;
      }
      case 'KOLIBRI': {
        offerStatusesConfig = OFFER_STATUSES_KOLIBRI;
        break;
      }
      default: {
        offerStatusesConfig = OFFER_STATUSES;
        break;
      }
    }

    return offerStatusesConfig;
  }

  isKolibriTenant(): boolean {
    const user = this.accountService.getUser();
    return user?.customerData?.name === 'KOLIBRI';
  }

  generateOfferShortGuidUrl(guid = '', metadata: any = {}): string {
    let url = '';

    if (guid) {
      if (this.generateUserUiUrl().indexOf('?') > -1) {
        url = `${this.generateUserUiUrl()}&s=${guid}`;
      } else {
        url = `${this.generateUserUiUrl()}?s=${guid}`;
      }
    }

    if (metadata.utm_source) {
      url += `&utm_source=${metadata.utm_source}`;
    }

    return url;
  }

  fetchSsn(): string {
    const ssn = this.currentOfferData.ssn ? this.currentOfferData.ssn :
      this.currentOfferData.offerJson &&
      this.currentOfferData.offerJson.acceptOfferData &&
      this.currentOfferData.offerJson.acceptOfferData.ssn
        ? this.currentOfferData.offerJson.acceptOfferData.ssn : '';

    return ssn;
  }

  getValueTranslation(value): any {
    return this.translateService.instant(`${this.profileService.getPropertyValueMapListName()}.${value + ''.toUpperCase()}`);
  }

  getPropertyValue(value: any, propertyConfig, propertiesData): any {
    const name = propertyConfig ? propertyConfig.name : '';
    const key = propertyConfig ? propertyConfig.key : '';

    if (propertyConfig && propertyConfig.inputType === 'select_data_list' && propertyConfig.settings && propertyConfig.settings.list) {
      let settingsList = [];

      if (propertyConfig.customValueConfig) {
        Object.keys(propertyConfig.customValueConfig).forEach(propertKey => {
          settingsList = settingsList.concat(propertyConfig.customValueConfig[propertKey].settings.list);
        });
      } else {
        settingsList = propertyConfig.settings.list;
      }

      const selectedItem = settingsList.find(item => item.value === value);

      if (selectedItem) {
        return this.translateService.instant(selectedItem.key);
      }
    }

    if (key === 'BOAT_LENGTH') {
      return value + ' fot';
    }

    if (key === 'BOAT_ENGINE_HP') {
      return value + ' hk';
    }

    if (key === 'BONUS') {
      if (propertiesData.vehicleBonusYears) {
        return `${value}% ${propertiesData.vehicleBonusYears} år`;
      } else {
        return `${value}%`;
      }
    }

    if (name === 'travelDuration' && propertiesData.travelDurationType === 'DAYS') {
      return `${value} ${this.translateService.instant(`PROPERTIES_VALUES_CUSTOM.DAYS`)}`;
    } else {
      return this.getValueTranslation(value).indexOf(this.profileService.getPropertyValueMapListName()) === -1 ?
        this.getValueTranslation(value) : value;
    }
  }

  getAdvisorDeclinedDialogContext(
    currentStatus?: string
  ): { reasonKeys: string[]; titleKey: string; reasonTranslationPrefix: string; infoTextKey?: string } | null {
    if (!currentStatus) {
      return null;
    }

    if ((KOLIBRI_DECLINED_ADVISOR_REASON_STATUS_KEYS_1_TO_7 as readonly string[]).includes(currentStatus)) {
      return {
        reasonKeys: KOLIBRI_DECLINED_ADVISOR_REASON_KEY_LIST_1_TO_7,
        titleKey: 'STATUS_CHANGE_REASONS.ADVISOR_DECLINED.TITLE',
        reasonTranslationPrefix: 'STATUS_CHANGE_REASONS.ADVISOR_DECLINED.REASON',
        infoTextKey: currentStatus === 'INIT_SESSION'
          ? 'STATUS_CHANGE_REASONS.ADVISOR_DECLINED.INIT_SESSION_INFO'
          : undefined,
      };
    }

    if ((KOLIBRI_DECLINED_ADVISOR_REASON_STATUS_KEYS_8_TO_10 as readonly string[]).includes(currentStatus)) {
      return {
        reasonKeys: KOLIBRI_DECLINED_ADVISOR_REASON_KEY_LIST_8_TO_10,
        titleKey: 'STATUS_CHANGE_REASONS.ADVISOR_DECLINED.TITLE',
        reasonTranslationPrefix: 'STATUS_CHANGE_REASONS.ADVISOR_DECLINED.REASON',
      };
    }

    return null;
  }

  buildOfferStatusReasonMetadataSection(
    metadata: OfferMetadata | undefined,
    metadataKey: string,
    titleKey: string,
    reasonTranslationPrefix: string,
  ): { titleKey: string; reasonTranslationPrefix: string; reasons: Array<{ reasonKey: string; otherText?: string }> } | null {
    const metadataValue = metadata?.[metadataKey];
    if (!metadataValue) {
      return null;
    }

    const metadataItems = Array.isArray(metadataValue) ? metadataValue : [metadataValue];
    const reasons = metadataItems.filter(item => item && item.reasonKey);
    if (!reasons.length) {
      return null;
    }

    return {
      titleKey,
      reasonTranslationPrefix,
      reasons,
    };
  }

  /** Server-sent events from `/api/private/sse/subscribe` (offer price events). */
  getOfferPriceSseEvents(offerId: string): Observable<SseEventEnvelope<OfferPriceSsePayload>> {
    return this.sseService.subscribe<SseEventEnvelope<OfferPriceSsePayload | string>>({
      channel: this.offerPriceSseChannel,
      path: this.config.sseSubscribeUrl,
      tokenQueryParamName: 'api_token',
      eventName: OFFER_PRICE_SSE_EVENT_NAMES,
    }).pipe(
      map((event: SseEventEnvelope<OfferPriceSsePayload | string>) => ({
        ...event,
        payload: this.normalizeSsePayload(event?.payload),
      })),
      filter((event: SseEventEnvelope<OfferPriceSsePayload | null>): event is SseEventEnvelope<OfferPriceSsePayload> => {
        if (!event?.payload) {
          return false;
        }
        return this.extractOfferIdFromSsePayload(event.payload) === `${offerId}`;
      })
    );
  }

  extractOfferIdFromSsePayload(
    payload: OfferPriceSsePayload | null | undefined,
    fallbackOfferId: string | number = '',
  ): string {
    if (payload?.offerId != null) {
      return `${payload.offerId}`;
    }
    return `${fallbackOfferId ?? ''}`;
  }

  extractErrorMessageFromSsePayload(payload: OfferPriceSsePayload | null | undefined): string {
    const message = payload?.message;
    return typeof message === 'string' && message.trim() ? message : '';
  }

  private normalizeSsePayload(
    payload: OfferPriceSsePayload | string | null | undefined,
  ): OfferPriceSsePayload | null {
    if (payload == null) {
      return null;
    }
    if (typeof payload !== 'string') {
      return payload;
    }

    try {
      return JSON.parse(payload);
    } catch (error) {
      return null;
    }
  }

  closePrivateHubSse(): void {
    this.sseService.unsubscribe(this.offerPriceSseChannel);
  }

}
